import { addClass, removeClass, throttle, offset } from '@tyler-components-web/core';
/** Generates random characters. Defaults to a length of 5. */
export function randomChars(length) {
    if (length === void 0) { length = 5; }
    return Math.random().toString(36).substr(2, length);
}
/** Toggles a CSS class (or classes) on an element based on a boolean. */
export function toggleClass(el, hasClass, className) {
    if (hasClass) {
        addClass(className, el);
    }
    else {
        removeClass(className, el);
    }
}
/** Toggles a value-less attribute on an element. */
export function toggleAttribute(el, hasAttribute, name, value) {
    if (value === void 0) { value = ''; }
    if (hasAttribute) {
        el.setAttribute(name, value);
    }
    else {
        el.removeAttribute(name);
    }
}
/**
 * Creates an element from an HTML string.
 */
export function elementFromHTML(html) {
    var template = document.createElement('template');
    html = html.trim();
    template.innerHTML = html;
    return template.content.firstElementChild;
}
export function createVisuallyHiddenElement() {
    var div = document.createElement('div');
    div.setAttribute('data-tcw-live-announcer', '');
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.height = '1px';
    div.style.width = '1px';
    div.style.padding = '1px';
    div.style.overflow = 'hidden';
    div.style.clip = 'rect(0px, 0px, 0px, 0px)';
    div.style.whiteSpace = 'nowrap';
    div.style.border = '0px';
    return div;
}
export function ensureInputElement(host) {
    return new Promise(function (resolve) {
        var element = host.querySelector('input');
        if (element) {
            resolve(element);
        }
        var observer = new MutationObserver(function (changes) {
            var hasAddedNodes = changes.reduce(function (prev, curr) { return prev + curr.addedNodes.length; }, 0) > 0;
            if (hasAddedNodes) {
                var foundElement = host.querySelector('input');
                if (foundElement) {
                    observer.disconnect();
                    resolve(foundElement);
                }
            }
        });
        observer.observe(host, { childList: true, subtree: true });
    });
}
export function coerceNumberArray(strOrNumOrArray) {
    if (!strOrNumOrArray) {
        return [];
    }
    if (typeof strOrNumOrArray === 'string') {
        return strOrNumOrArray.replace(/ |\[|]|\"/g, '').split(',').map(function (n) { return Number(n); });
    }
    else if (typeof strOrNumOrArray === 'number') {
        return [strOrNumOrArray];
    }
    else {
        return strOrNumOrArray.map(function (n) { return Number(n); });
    }
}
/**
 * Compares two objects for deep equality.
 * @param a
 * @param b
 */
export function isDeepEqual(a, b) {
    return a === b || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
}
/** Determines if the provided string value can be parsed into a valid numeric value. */
export function isNumeric(str) {
    if (typeof str !== 'string') {
        return false;
    }
    return !isNaN(str) && !isNaN(parseFloat(str));
}
/**
 * Highlights text in the given label by converting it to HTML and using a `<span>` tag to show the highlighted text within the original label.
 * @param label The full text.
 * @param highlightText The text to highlight.
 */
export function highlightTextHTML(label, highlightText) {
    var text = label.toLowerCase();
    var startIndex = text.indexOf(highlightText.toLowerCase());
    if (startIndex !== -1) {
        var endIndex = startIndex + highlightText.length;
        var wrapperSpan = document.createElement('span');
        var highlightSpan = document.createElement('span');
        highlightSpan.style.fontWeight = 'bold';
        highlightSpan.textContent = label.substring(startIndex, endIndex);
        wrapperSpan.appendChild(document.createTextNode(label.substring(0, startIndex)));
        wrapperSpan.appendChild(highlightSpan);
        wrapperSpan.appendChild(document.createTextNode(label.substring(endIndex)));
        return wrapperSpan;
    }
    return undefined;
}
/*
 * Watches for user events to detect if browser is idle.
 *
 * @param callback The function to call when the browser becomes idle.
 * @param timespan The time to wait before the browser is considered idle.
 */
export function idleWatch(callback, timespan) {
    var events = [
        'mousedown',
        'mousemove',
        'touchstart',
        'touchmove',
        'keydown',
        'wheel',
        'resize'
    ];
    events.forEach(function (e) {
        window.addEventListener(e, throttle(function (event) {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
            timeoutId = window.setTimeout(callback, timespan);
        }, 1000, true), true);
    });
    var timeoutId = window.setTimeout(callback, timespan);
}
/**
 * Attempts to scroll a target element into view within a scrollable parent element, unless already visible within the container.
 * @param scrollElement The scrollable parent element.
 * @param targetElement The element to scroll into view.
 * @param behavior The scroll behavior. Defaults to 'auto'.
 * @param block The block position to anchor the target element to within the scroll element.
 */
export function tryScrollIntoView(scrollElement, targetElement, behavior, block) {
    if (behavior === void 0) { behavior = 'auto'; }
    if (block === void 0) { block = 'nearest'; }
    if (!scrollElement) {
        return;
    }
    var isScrollable = scrollElement.scrollHeight > scrollElement.clientHeight || scrollElement.scrollWidth > scrollElement.clientWidth;
    if (isScrollable) {
        var offsetRect = offset(targetElement, scrollElement);
        var isClippedTop = offsetRect.top <= targetElement.clientHeight;
        var isClippedBottom = offsetRect.bottom <= targetElement.clientHeight;
        if (isClippedTop || isClippedBottom) {
            var top_1 = calcBlockScroll(block, isClippedTop, targetElement.offsetTop, targetElement.clientHeight, scrollElement.offsetTop, scrollElement.offsetHeight);
            scrollElement.scrollTo({ top: top_1, behavior: behavior });
            return;
        }
        var isClippedLeft = offsetRect.left <= targetElement.clientWidth;
        var isClippedRight = offsetRect.right <= targetElement.clientWidth;
        if (isClippedLeft || isClippedRight) {
            var left = calcBlockScroll(block, isClippedLeft, targetElement.offsetLeft, targetElement.clientWidth, scrollElement.offsetLeft, scrollElement.offsetWidth);
            scrollElement.scrollTo({ left: left, behavior: behavior });
        }
    }
}
/** Calculates the block anchor position for a target element within a scrollable parent element. */
export function calcBlockScroll(block, isClippedStart, targetOffset, targetSize, scrollOffset, scrollSize) {
    if (block === 'nearest') {
        if (isClippedStart) {
            return (targetOffset - scrollOffset) - targetSize;
        }
        return (targetOffset - scrollSize) + targetSize * 2;
    }
    return targetOffset - scrollOffset - scrollSize / 2 + targetSize / 2;
}
